import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Card } from 'core/components/card';
import { appConfig } from 'brand-config';
import { awsDateToStringDate } from 'core/helpers/formatters';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import useBrandFeatures from 'core/hooks/use-brand-features';

import notificationIconWhite from './alert-small-white.svg';
import useStyles from './future-effective-date-warning.styles';

const FutureEffectiveDateWarning = ({ daysOut, isAQDState = false, daysUntilEffective, creationDate }) => {
  const classes = useStyles();
  const { user, isAgency } = useSession();
  const {
    discounts: { hasAdvanceQuoteDiscount }
  } = useBrandFeatures();

  const showAQDMessage =
    hasAdvanceQuoteDiscount &&
    isAQDState &&
    creationDate &&
    typeof daysUntilEffective === 'number' &&
    !Number.isNaN(daysUntilEffective) &&
    daysUntilEffective >= 0 &&
    daysUntilEffective < 8;

  const formattedCreationDate = creationDate ? awsDateToStringDate(creationDate.toISOString().slice(0, 10)) : '';

  useEffect(() => {
    if (showAQDMessage) {
      track('Staff Appcues: AQD banner shown in Auto Settings', {
        isAgency,
        affinityCode: user.affinityCode
      });
    }
  }, [showAQDMessage, isAgency, user.affinityCode]);

  // Brands with role-based effective date rules (e.g. Forreston, #29728) don't follow the
  // "must be future effective" rule, so the generic blurb is wrong there (#31675):
  //  - Underwriters have no date restrictions → no blurb at all.
  //  - Agents are bounded by maxBackdateDays/maxForwardDays (enforced by the date picker)
  //    → show a neutral line describing their actual window instead.
  const effectiveDateRules = appConfig?.effectiveDateRules;
  if (effectiveDateRules) {
    const agentRules = isAgency && effectiveDateRules.agent?.newBusiness;
    if (!agentRules) {
      return null;
    }
    const { maxBackdateDays, maxForwardDays } = agentRules;
    return (
      <Card className={classes.warning}>
        <img src={notificationIconWhite} alt="Notification Icon" />
        {`Effective date can be up to ${maxBackdateDays} day${maxBackdateDays === 1 ? '' : 's'} in the past and up to ${maxForwardDays} day${maxForwardDays === 1 ? '' : 's'} in the future.`}
      </Card>
    );
  }

  let message = '';
  if (showAQDMessage) {
    message = `Policies must start at least ${daysOut} day${
      daysOut === 1 ? '' : 's'
    } in the future. An Advance Quote discount applies for effective dates 3+ days from the offer's creation date (${formattedCreationDate}) and increases for dates 8+ days from the creation date.`;
  } else {
    message = `All policies must be at least ${daysOut} day${daysOut === 1 ? '' : 's'} future effective.`;
  }

  return (
    <Card className={classes.warning}>
      <img src={notificationIconWhite} alt="Notification Icon" />
      {message}
    </Card>
  );
};

FutureEffectiveDateWarning.propTypes = {
  daysOut: PropTypes.number,
  isAQDState: PropTypes.bool,
  daysUntilEffective: PropTypes.number,
  creationDate: PropTypes.instanceOf(Date)
};

FutureEffectiveDateWarning.defaultProps = {
  daysOut: undefined,
  isAQDState: false,
  daysUntilEffective: undefined,
  creationDate: undefined
};

export default FutureEffectiveDateWarning;
